/**
 * CSRF Protection Utilities
 *
 * Tokens are stored in localStorage (session-scoped, ~1h TTL). They are not
 * persisted to Firestore — the previous Firestore path used a literal
 * `users/anonymous/...` doc when auth was not ready, which always fails
 * security rules for real signed-in users.
 */

import { auth } from '../firebase/firebaseConfig';

const CSRF_KEY_PREFIX = 'flashcode_csrf_';

/**
 * Stable session fragment for token keys and validation.
 * @returns {string}
 */
const csrfSessionKey = () => (auth.currentUser ? auth.currentUser.uid : 'anonymous');

const csrfStorageKeys = () => {
  const u = csrfSessionKey();
  return {
    tokenKey: `${CSRF_KEY_PREFIX}${u}_token`,
    expiryKey: `${CSRF_KEY_PREFIX}${u}_expiry`,
  };
};

const readLocal = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeLocal = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (error) {
    console.warn('Failed to store CSRF token in localStorage:', error);
  }
};

const removeLocal = (key) => {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
};

/**
 * Generate a CSRF token
 *
 * @returns {{ token: string, expiry: number }}
 */
export const generateCsrfToken = () => {
  const randomPart = Math.random().toString(36).substring(2, 15);
  const timestamp = Date.now();
  const uid = csrfSessionKey();
  const tokenData = `${randomPart}|${timestamp}|${uid}`;
  const token = btoa(tokenData);
  const expiry = timestamp + 60 * 60 * 1000;
  return { token, expiry };
};

/**
 * Validate a CSRF token
 *
 * @param {string} token
 * @returns {boolean}
 */
export const validateCsrfToken = (token) => {
  try {
    const tokenData = atob(token);
    const parts = tokenData.split('|');

    if (parts.length !== 3) {
      return false;
    }

    const [, timestamp, uid] = parts;
    const tokenTime = parseInt(timestamp, 10);
    const currentTime = Date.now();

    if (isNaN(tokenTime) || currentTime - tokenTime > 60 * 60 * 1000) {
      return false;
    }

    if (uid !== csrfSessionKey()) {
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error validating CSRF token:', error);
    return false;
  }
};

/**
 * Store CSRF token in localStorage.
 *
 * @param {string} token
 * @param {number} expiry
 */
export const storeCsrfToken = async (token, expiry) => {
  const { tokenKey, expiryKey } = csrfStorageKeys();
  writeLocal(tokenKey, token);
  writeLocal(expiryKey, expiry.toString());
};

/**
 * Retrieve CSRF token from localStorage.
 *
 * @returns {Promise<string|null>}
 */
export const retrieveCsrfToken = async () => {
  try {
    const { tokenKey, expiryKey } = csrfStorageKeys();
    const token = readLocal(tokenKey);
    const expiryStr = readLocal(expiryKey);

    if (!token || !expiryStr) {
      return null;
    }

    const expiry = parseInt(expiryStr, 10);
    const now = Date.now();

    if (isNaN(expiry) || now >= expiry) {
      removeLocal(tokenKey);
      removeLocal(expiryKey);
      return null;
    }

    return token;
  } catch (error) {
    console.error('Error retrieving CSRF token:', error);
    return null;
  }
};

/**
 * Get a valid CSRF token, generating a new one if needed.
 *
 * @returns {Promise<string>}
 */
export const getOrCreateCsrfToken = async () => {
  try {
    let token = await retrieveCsrfToken();

    if (!token) {
      const { token: newToken, expiry } = generateCsrfToken();
      await storeCsrfToken(newToken, expiry);
      token = newToken;
    }

    return token;
  } catch (error) {
    console.error('Error in getOrCreateCsrfToken:', error);
    const { token } = generateCsrfToken();
    return token;
  }
};

/**
 * Add CSRF token to a request object (for fetch or axios).
 *
 * @param {Object} options
 * @returns {Promise<Object>}
 */
export const addCsrfToken = async (options = {}) => {
  const token = await getOrCreateCsrfToken();
  const headers = options.headers || {};
  headers['X-CSRF-Token'] = token;
  return { ...options, headers };
};
